import { useEffect, useState } from "react";
import { getContent } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { FadeIn, SectionTag } from "@/components/Reveal";
import RoadAnatomy from "@/components/RoadAnatomy";
import SEO from "@/components/SEO";

export default function About() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    getContent().then(setContent).catch(() => setContent({}));
  }, []);

  if (!content) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="about-loading" />;
  const about = content.about;

  return (
    <main data-testid="about-page">
      <SEO
        title="About Catalyst Petrochem — Bitumen & Petrochemical Leaders"
        description="Three decades of bitumen import expertise, powering India's roads. Meet the leadership and story behind Catalyst Petrochem LLP."
        path="/about"
      />
      <PageHeader
        index="01"
        tag="About Us"
        title="The catalyst behind India's roads."
        description={about.intro}
      />

      <RoadAnatomy />

      <section className="px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="manifesto-chapters">
        <SectionTag index="03" label="Manifesto" />
        <div className="mt-16">
          {about.chapters.map((ch, i) => (
            <FadeIn key={ch.num} delay={0.04 * i}>
              <div
                className="grid gap-6 border-t border-white/10 py-12 last:border-b md:grid-cols-12 md:gap-10"
                data-testid={`about-chapter-${ch.num}`}
              >
                <p className="font-mono text-sm text-brand md:col-span-2">/{ch.num}</p>
                <h2 className="font-display text-3xl font-semibold uppercase tracking-normal md:col-span-4 md:text-4xl">
                  {ch.title}
                </h2>
                <p className="text-sm leading-relaxed text-neutral-400 md:col-span-6 md:text-base">{ch.text}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#0C0C0C] px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="leadership-section">
        <SectionTag index="04" label="Leadership" />
        <div className="mt-16 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          {[
            { name: "Sanjay Goyal", role: "Partner", note: "A pioneering force in the bitumen trade with over three decades of industry authority, architecting resilient global sourcing networks and powering India's largest infrastructure corridors." },
            { name: "Nipun Goyal", role: "Partner", note: "Champions technological innovation, driving high-value international trade partnerships and modernizing polymer-modified bitumen operations to meet next-generation road standards." },
            { name: "Himanshu Garg", role: "Chief Executive Officer", note: "Orchestrates end-to-end supply chain logistics, global procurement, and nationwide distribution networks to guarantee seamless operational precision at scale." },
            { name: "Sanchit Goyal", role: "Executive Director", note: "Steers forward-looking growth strategies, integrating modern market dynamics with legacy operational strengths to shape the future of industrial petrochemical solutions." },
          ].map((p, i) => (
            <FadeIn key={p.name} delay={i * 0.08} className="bg-[#0A0A0A] p-10 md:p-14">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">{p.role}</p>
              <h3 className="mt-4 font-display text-3xl font-semibold uppercase tracking-normal md:text-4xl">{p.name}</h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-500">{p.note}</p>
            </FadeIn>
          ))}
        </div>
      </section>
    </main>
  );
}
